import React, { useEffect, useState } from "react";
import apiClient from "../../pages/keycloak/axiosInstance";
import { motion, AnimatePresence } from "framer-motion";

// ── StatusBadge ──────────────────────────────────────────────────────────────
// Known statuses have explicit colours; anything else renders as-is with a
// neutral slate style so future statuses are never silently swallowed.
const STATUS_CONFIG = {
  // PDI Review statuses
  approved:                   { label: "Approved",                   cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  Approved:                   { label: "Approved",                   cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  "Approved with Failed Tests": { label: "Approved with Failed Tests", cls: "bg-orange-100 text-orange-700 border-orange-200" },
  rejected:                   { label: "Rejected",                   cls: "bg-red-100 text-red-700 border-red-200" },
  Rejected:                   { label: "Rejected",                   cls: "bg-red-100 text-red-700 border-red-200" },
  "Accepted on Deviation":    { label: "Accepted on Deviation",      cls: "bg-amber-100 text-amber-700 border-amber-200" },
  Pending:                    { label: "Pending",                    cls: "bg-gray-100 text-gray-600 border-gray-200" },

  // MSPL-specific raw values from the API
  approved_on_deviation:      { label: "Accepted on Deviation",      cls: "bg-amber-100 text-amber-700 border-amber-200" },
};

const StatusBadge = ({ status }) => {
  const c = STATUS_CONFIG[status];
  // Unknown status → show the raw value with a neutral style
  if (!c) {
    return (
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border bg-slate-100 text-slate-600 border-slate-200">
        {status}
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${c.cls}`}>
      {c.label}
    </span>
  );
};

// ── ResponseBanner ───────────────────────────────────────────────────────────
const ResponseBanner = ({ message, onClose }) => {
  if (!message) return null;

  const isReject  = message.action === "rejected";
  const isDeviate = message.action === "accepted_on_deviation";

  const colourCls = isReject
    ? "bg-red-50 border-red-200 text-red-800"
    : isDeviate
    ? "bg-amber-50 border-amber-200 text-amber-800"
    : "bg-blue-50 border-blue-200 text-blue-800";

  const countLabel = isReject
    ? "PDIs Rejected Count"
    : isDeviate
    ? "PDIs Accepted on Deviation Count"
    : "PDIs Approved Count";

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className={`mb-5 p-4 rounded-xl border flex items-start justify-between gap-3 shadow-sm ${colourCls}`}
    >
      <div className="text-sm space-y-1">
        <p className="font-semibold">{message.message}</p>
        <p className="opacity-80">
          <span className="font-medium">{countLabel}:</span> {message.count}
        </p>
      </div>
      <button onClick={onClose} className="opacity-50 hover:opacity-100 transition-opacity text-lg leading-none mt-0.5">×</button>
    </motion.div>
  );
};

// ── Button disable logic based on mspl_status ────────────────────────────────
//
//  mspl_status        | Approve | Reject | Deviation
//  -------------------|---------|--------|----------
//  Approved           |   ✗     |   ✗    |   ✗
//  Rejected           |   ✓     |   ✗    |   ✓
//  Accepted/Deviation |   ✓     |   ✗    |   ✗
//  anything else      |   ✓     |   ✓    |   ✓
//
const resolvedMsplStatus = (raw) => STATUS_CONFIG[raw]?.label ?? raw ?? "Pending";

const actionDisabled = (msplRaw, action, canEdit, isLoading) => {
  if (!canEdit || isLoading) return true;
  const resolved = resolvedMsplStatus(msplRaw);

  if (resolved === "Approved") return true; // all disabled

  if (resolved === "Rejected") {
    return action === "rejected"; // only Reject disabled
  }

  if (resolved === "Accepted on Deviation") {
    return action !== "approved"; // only Approve enabled
  }

  return false; // Pending or unknown → all enabled
};

// ── Main component ────────────────────────────────────────────────────────────
const MsplApprovals = ({ switchToTab }) => {
  const [data, setData]                       = useState([]);
  const [selectedFiles, setSelectedFiles]     = useState({});
  const [loading, setLoading]                 = useState({});
  const [responseMessage, setResponseMessage] = useState(null);
  const [canEditMspl, setCanEditMspl]         = useState(false);

  useEffect(() => {
    const storedRoles = sessionStorage.getItem("userRoles");
    if (storedRoles) {
      let roles = [];
      try {
        const parsed = JSON.parse(storedRoles);
        if (Array.isArray(parsed)) roles = parsed;
      } catch {
        roles = storedRoles.split(",").map(r => r.trim());
      }
      setCanEditMspl(roles.includes("MSPL_Approvals_Upload_Edit"));
    }
  }, []);

  useEffect(() => {
    const fetchApprovalData = async () => {
      try {
        const projectId = localStorage.getItem("selectedProject");
        const response  = await apiClient.get(`get_pdi_witness`, { params: { projectId } });
        setData(response.data.data);
      } catch (error) {
        console.error("Error fetching approval data:", error);
      }
    };
    fetchApprovalData();
  }, []);

  const handleAction = async (id, action) => {
    if (!canEditMspl) return;
    setLoading(prev => ({ ...prev, [id]: action }));

    const statusMap = {
      approved:              "approved",
      rejected:              "Rejected",
      accepted_on_deviation: "approved_on_deviation",
    };

    try {
      const uniqueId = data.find(e => e.id === id).uniqueId;
      const response = await apiClient.post(`update_pdi_mspl_approval`, {
        projectId: localStorage.getItem("selectedProject"),
        fileName:  uniqueId,
        mspl_approval: action,
      });

      setResponseMessage({
        action,
        message: response.data.message,
        count:   response.data.pdi_updated_count,
      });
      setTimeout(() => setResponseMessage(null), 20000);

      setData(prev =>
        prev.map(entry =>
          entry.id === id
            ? { ...entry, mspl_status: statusMap[action] ?? entry.mspl_status }
            : entry
        )
      );
    } catch (error) {
      console.error("Error updating MSPL status:", error);
      setResponseMessage({ action, message: "Failed to update MSPL status.", count: 0 });
      setTimeout(() => setResponseMessage(null), 5000);
    } finally {
      setLoading(prev => ({ ...prev, [id]: null }));
    }
  };

  const handleFileUpload = async (entry) => {
    if (!canEditMspl) return;
    const files = selectedFiles[entry.id];
    if (!files || files.length === 0) { alert("Please select files to upload."); return; }

    setLoading(prev => ({ ...prev, [entry.id]: "upload" }));
    const formData = new FormData();
    formData.append("projectId",    localStorage.getItem("selectedProject"));
    formData.append("supplierName", entry.supplierName);
    formData.append("uniqueId",     entry.uniqueId);
    Array.from(files).forEach(file => formData.append("documents", file));

    try {
      await apiClient.post(`upload_mspl_document`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      alert("Files uploaded successfully!");
      setSelectedFiles(prev => ({ ...prev, [entry.id]: [] }));
    } catch (error) {
      console.error("Error uploading documents:", error);
      alert("Failed to upload files.");
    } finally {
      setLoading(prev => ({ ...prev, [entry.id]: null }));
    }
  };

  const handleFileSelect = (id, files) => {
    if (!canEditMspl) return;
    setSelectedFiles(prev => ({ ...prev, [id]: files }));
  };

  const handleViewPDF = (uniqueId) => {
    localStorage.setItem("viewUniqueId", uniqueId);
    if (switchToTab) switchToTab(2);
    setTimeout(() => localStorage.removeItem("viewUniqueId"), 5000);
  };

  // Shared action button styles
  const actionBtn = (colour, disabled) =>
    `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-all duration-150 shadow-sm whitespace-nowrap
     ${colour}
     ${disabled ? "opacity-40 cursor-not-allowed" : "hover:brightness-110 active:scale-95"}`;

  // Spinner SVG
  const Spinner = () => (
    <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
    </svg>
  );

  return (
    <div className="p-5 bg-gray-50 min-h-screen">

      {/* Header */}
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 tracking-tight">MSPL Approvals</h2>
          <p className="text-sm text-gray-400 mt-0.5">Review and approve PDI witness records</p>
        </div>
        {!canEditMspl && (
          <div className="flex items-center gap-2 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg">
            <svg className="w-4 h-4 shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
            </svg>
            View only — no edit permission
          </div>
        )}
      </div>

      {/* Response banner */}
      <AnimatePresence>
        {responseMessage && (
          <ResponseBanner message={responseMessage} onClose={() => setResponseMessage(null)} />
        )}
      </AnimatePresence>

      {/* Table card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#424752] text-white text-xs uppercase tracking-wide">
                {["Unique ID", "Supplier Name", "Date", "PDI Review Status", "MSPL Status", "Actions", "Upload Files", "View"].map(h => (
                  <th key={h} className="px-4 py-3.5 text-left font-semibold whitespace-nowrap first:pl-5 last:pr-5">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data.length > 0 ? data.map((entry, idx) => {
                const isRowLoading = loading[entry.id] != null;

                const approveDisabled  = actionDisabled(entry.mspl_status, "approved",              canEditMspl, isRowLoading);
                const rejectDisabled   = actionDisabled(entry.mspl_status, "rejected",              canEditMspl, isRowLoading);
                const deviateDisabled  = actionDisabled(entry.mspl_status, "accepted_on_deviation", canEditMspl, isRowLoading);

                return (
                  <motion.tr
                    key={entry.id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.03 }}
                    className="hover:bg-gray-50/70 transition-colors"
                  >
                    {/* Unique ID */}
                    <td className="px-4 py-3.5 pl-5 font-mono text-md text-gray-600 whitespace-nowrap">{entry.uniqueId}</td>

                    {/* Supplier Name */}
                    <td className="px-4 py-3.5 font-medium text-gray-800 whitespace-nowrap">{entry.supplierName}</td>

                    {/* Date */}
                    <td className="px-4 py-3.5 text-gray-500 whitespace-nowrap">{entry.date}</td>

                    {/* PDI Review Status — shows raw value if unknown */}
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <StatusBadge status={entry.status} />
                    </td>

                    {/* MSPL Status — shows raw value if unknown */}
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <StatusBadge status={entry.mspl_status || "Pending"} />
                    </td>

                    {/* Actions — flex-nowrap forces single line; parent cell scrolls */}
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <div className="flex items-center gap-1.5 flex-nowrap">

                        {/* Approve */}
                        <button
                          className={actionBtn("bg-emerald-500", approveDisabled)}
                          onClick={() => handleAction(entry.id, "approved")}
                          disabled={approveDisabled}
                        >
                          {loading[entry.id] === "approved" ? <Spinner /> : (
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
                            </svg>
                          )}
                          Approve
                        </button>

                        {/* Reject */}
                        <button
                          className={actionBtn("bg-red-500", rejectDisabled)}
                          onClick={() => handleAction(entry.id, "rejected")}
                          disabled={rejectDisabled}
                        >
                          {loading[entry.id] === "rejected" ? <Spinner /> : (
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                            </svg>
                          )}
                          Reject
                        </button>

                        {/* Deviation */}
                        <button
                          className={actionBtn("bg-amber-500", deviateDisabled)}
                          onClick={() => handleAction(entry.id, "accepted_on_deviation")}
                          disabled={deviateDisabled}
                        >
                          {loading[entry.id] === "accepted_on_deviation" ? <Spinner /> : (
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 100 18A9 9 0 0012 3z"/>
                            </svg>
                          )}
                          Deviation
                        </button>

                      </div>
                    </td>

                    {/* Upload Files */}
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <div className="flex items-center gap-2 flex-nowrap">
                        <label className={`cursor-pointer flex items-center gap-1.5 px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600 bg-gray-50 hover:bg-gray-100 transition-colors whitespace-nowrap
                          ${!canEditMspl ? "opacity-40 cursor-not-allowed pointer-events-none" : ""}`}>
                          <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"/>
                          </svg>
                          {selectedFiles[entry.id]?.length > 0
                            ? `${selectedFiles[entry.id].length} file${selectedFiles[entry.id].length > 1 ? "s" : ""}`
                            : "Choose"}
                          <input
                            type="file"
                            multiple
                            className="hidden"
                            disabled={!canEditMspl}
                            onChange={(e) => handleFileSelect(entry.id, e.target.files)}
                          />
                        </label>
                        <button
                          className={actionBtn("bg-[#424752]", !canEditMspl || loading[entry.id] === "upload")}
                          onClick={() => handleFileUpload(entry)}
                          disabled={!canEditMspl || loading[entry.id] === "upload"}
                        >
                          {loading[entry.id] === "upload" ? <Spinner /> : (
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/>
                            </svg>
                          )}
                          Upload
                        </button>
                      </div>
                    </td>

                    {/* View */}
                    <td className="px-4 py-3.5 pr-5 whitespace-nowrap">
                      <button
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-[#424752] hover:brightness-110 active:scale-95 transition-all shadow-sm whitespace-nowrap"
                        onClick={() => handleViewPDF(entry.uniqueId)}
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                        </svg>
                        View
                      </button>
                    </td>
                  </motion.tr>
                );
              }) : (
                <tr>
                  <td colSpan="8" className="px-5 py-14 text-center text-gray-400 text-sm">
                    <svg className="w-10 h-10 mx-auto mb-3 opacity-20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                    </svg>
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MsplApprovals;

// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import apiClient from "../../pages/keycloak/axiosInstance";

// const MsplApprovals = ({ switchToTab }) => {
//   const [data, setData] = useState([]);
//   const [selectedFiles, setSelectedFiles] = useState({});
//   const [loading, setLoading] = useState({});
//   const [responseMessage, setResponseMessage] = useState(null);
//   const [canEditMspl, setCanEditMspl] = useState(false); // ← Role check
//   const baseURL = process.env.REACT_APP_API_BASE_URL;

//   // Check for MSPL_Approvals_Edit role
//   useEffect(() => {
//     const storedRoles = sessionStorage.getItem("userRoles");
//     if (storedRoles) {
//       let roles = [];
//       try {
//         const parsed = JSON.parse(storedRoles);
//         if (Array.isArray(parsed)) roles = parsed;
//       } catch {
//         roles = storedRoles.split(",").map(r => r.trim());
//       }
//       setCanEditMspl(roles.includes("MSPL_Approvals_Upload_Edit"));
//     }
//   }, []);

//   useEffect(() => {
//     const fetchApprovalData = async () => {
//       try {
//         const projectId = localStorage.getItem("selectedProject");
//         const response = await apiClient.get(`get_pdi_witness`, {
//           params: { projectId },
//         });
//         setData(response.data.data);
//       } catch (error) {
//         console.error("Error fetching approval data:", error);
//       }
//     };
//     fetchApprovalData();
//   }, []);

//   const handleAction = async (id, action) => {
//     if (!canEditMspl) return; // Prevent action if no permission

//     setLoading((prev) => ({ ...prev, [id]: action }));
//     try {
//       const uniqueId = data.find((entry) => entry.id === id).uniqueId;
      
//       const response = await apiClient.post(`update_pdi_mspl_approval`, {
//         projectId: localStorage.getItem("selectedProject"),
//         fileName: uniqueId,
//         mspl_approval: action,
//       });

//       const statusMap = {
//         approved: "Approved",
//         rejected: "Rejected",
//         accepted_on_deviation: "Accepted on Deviation",
//       };
  
//       setResponseMessage({
//         message: response.data.message,
//         pdiApprovedCount: response.data.pdi_updated_count,
//       });

//       setTimeout(() => setResponseMessage(null), 20000);

//       setData((prevData) =>
//         prevData.map((entry) =>
//           entry.id === id
//             ? { ...entry, mspl_status: statusMap[action] || entry.mspl_status }
//             : entry
//         )
//       );
//     } catch (error) {
//       console.error("Error updating MSPL status:", error);
//       setResponseMessage({
//         message: "Failed to update MSPL status.",
//         pdiApprovedCount: 0,
//       });
//       setTimeout(() => setResponseMessage(null), 5000);
//     } finally {
//       setLoading((prev) => ({ ...prev, [id]: null }));
//     }
//   };

//   const handleFileUpload = async (entry) => {
//     if (!canEditMspl) return; // Prevent upload if no permission

//     setLoading((prev) => ({ ...prev, [entry.id]: "upload" }));
//     const { uniqueId, supplierName } = entry;
//     const projectId = localStorage.getItem("selectedProject");
//     const files = selectedFiles[entry.id];
  
//     if (files && files.length > 0) {
//       const formData = new FormData();
//       formData.append("projectId", projectId);
//       formData.append("supplierName", supplierName);
//       formData.append("uniqueId", uniqueId);
  
//       Array.from(files).forEach((file) => formData.append("documents", file));
  
//       try {
//         await apiClient.post(`upload_mspl_document`, formData, {
//           headers: { "Content-Type": "multipart/form-data" },
//         });
//         alert("Files uploaded successfully!");
//         setSelectedFiles((prev) => ({ ...prev, [entry.id]: [] }));
//       } catch (error) {
//         console.error("Error uploading documents:", error);
//         alert("Failed to upload files.");
//       } finally {
//         setLoading((prev) => ({ ...prev, [entry.id]: null }));
//       }
//     } else {
//       alert("Please select files to upload.");
//     }
//   };

//   const handleFileSelect = (id, files) => {
//     if (!canEditMspl) return; // Prevent file selection if no permission
//     setSelectedFiles((prev) => ({
//       ...prev,
//       [id]: files,
//     }));
//   };

//   const handleViewPDF = (uniqueId) => {
//     localStorage.setItem("viewUniqueId", uniqueId);
//     if (switchToTab) switchToTab(2);
//     setTimeout(() => localStorage.removeItem("viewUniqueId"), 5000);
//   };

//   return (
//     <div className="p-4 bg-gray-200">
//       <h2 className="text-xl font-bold mb-4">MSPL Approvals</h2>
//       {responseMessage && (
//         <div className="mb-4 p-4 bg-blue-100 text-blue-800 rounded-lg shadow-md">
//           <p>{responseMessage.message}</p>
//           <p><strong>PDIs Approved Count:</strong> {responseMessage.pdiApprovedCount}</p>
//         </div>
//       )}
//       <div className="overflow-x-auto">
//         <table className="w-full bg-white shadow-md rounded-lg overflow-hidden">
//           <thead className="bg-gray-800 text-white">
//             <tr>
//               <th className="p-3 text-left whitespace-nowrap">Unique ID</th>
//               <th className="p-3 text-left whitespace-nowrap">Supplier Name</th>
//               <th className="p-3 text-left whitespace-nowrap">Date</th>
//               <th className="p-3 text-left whitespace-nowrap">PDI Review Status</th>
//               <th className="p-3 text-left whitespace-nowrap">MSPL Status</th>
//               <th className="p-3 text-left whitespace-nowrap">Actions</th> {/* Always visible */}
//               <th className="p-3 text-left whitespace-nowrap">Upload Files</th> {/* Always visible */}
//               <th className="p-3 text-left whitespace-nowrap">View</th>
//             </tr>
//           </thead>
//           <tbody>
//             {data.length > 0 ? (
//               data.map((entry) => (
//                 <tr key={entry.id} className="border-b hover:bg-gray-100">
//                   <td className="p-3 whitespace-nowrap">{entry.uniqueId}</td>
//                   <td className="p-3 whitespace-nowrap">{entry.supplierName}</td>
//                   <td className="p-3 whitespace-nowrap">{entry.date}</td>
//                   <td className="p-3 whitespace-nowrap">
//                     <span className={`px-2 py-1 rounded ${entry.status === "approved" ? "bg-green-200 text-green-800" : "bg-red-200 text-red-800"}`}>
//                       {entry.status}
//                     </span>
//                   </td>
//                   <td className="p-3 whitespace-nowrap">
//                     <span className={`px-2 py-1 rounded ${
//                       entry.mspl_status === "Approved" ? "bg-green-200 text-green-800" :
//                       entry.mspl_status === "Rejected" ? "bg-red-200 text-red-800" :
//                       entry.mspl_status === "Accepted on Deviation" ? "bg-yellow-200 text-yellow-800" :
//                       "bg-gray-200 text-gray-800"
//                     }`}>
//                       {entry.mspl_status || "Pending"}
//                     </span>
//                   </td>

//                   {/* Actions column — visible but buttons disabled if no permission */}
//                   <td className="p-3 whitespace-nowrap">
//                     <button
//                       className={`bg-green-500 text-white py-1 px-3 rounded mr-2 ${!canEditMspl ? 'opacity-50 cursor-not-allowed' : ''}`}
//                       onClick={() => handleAction(entry.id, "approved")}
//                       disabled={!canEditMspl || loading[entry.id] != null}
//                     >
//                       {loading[entry.id] === "approved" ? "Loading..." : "Approve"}
//                     </button>
//                     <button
//                       className={`bg-red-500 text-white py-1 px-3 rounded mr-2 ${!canEditMspl ? 'opacity-50 cursor-not-allowed' : ''}`}
//                       onClick={() => handleAction(entry.id, "rejected")}
//                       disabled={!canEditMspl || loading[entry.id] != null}
//                     >
//                       {loading[entry.id] === "rejected" ? "Loading..." : "Reject"}
//                     </button>
//                     <button
//                       className={`bg-yellow-500 text-white py-1 px-3 rounded ${!canEditMspl ? 'opacity-50 cursor-not-allowed' : ''}`}
//                       onClick={() => handleAction(entry.id, "accepted_on_deviation")}
//                       disabled={!canEditMspl || loading[entry.id] != null}
//                     >
//                       {loading[entry.id] === "accepted_on_deviation" ? "Loading..." : "Accept on Deviation"}
//                     </button>
//                   </td>

//                   {/* Upload Files column — visible but disabled if no permission */}
//                   <td className="p-3 whitespace-nowrap">
//                     <div className="flex items-center space-x-2">
//                       <input
//                         type="file"
//                         multiple
//                         onChange={(e) => handleFileSelect(entry.id, e.target.files)}
//                         disabled={!canEditMspl}
//                         className={`bg-gray-200 rounded-l-md border ${!canEditMspl ? 'opacity-50 cursor-not-allowed' : ''}`}
//                       />
//                       <button
//                         className={`bg-[#424752] text-white py-1 px-3 rounded-r-md ${!canEditMspl ? 'opacity-50 cursor-not-allowed' : ''}`}
//                         onClick={() => handleFileUpload(entry)}
//                         disabled={!canEditMspl || loading[entry.id] === "upload"}
//                       >
//                         {loading[entry.id] === "upload" ? "Uploading..." : "Upload"}
//                       </button>
//                     </div>
//                   </td>

//                   <td className="p-3 whitespace-nowrap">
//                     <button
//                       className="bg-[#424752] text-white py-1 px-3 rounded"
//                       onClick={() => handleViewPDF(entry.uniqueId)}
//                     >
//                       View
//                     </button>
//                   </td>
//                 </tr>
//               ))
//             ) : (
//               <tr>
//                 <td colSpan="8" className="p-3 text-center text-gray-500">
//                   No records found
//                 </td>
//               </tr>
//             )}
//           </tbody>
//         </table>
//       </div>
//     </div>
//   );
// };

// export default MsplApprovals;